import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useLivePoll } from "../lib/live";
import { Card } from "../components/ui/card";
import { MapContainer, TileLayer, CircleMarker, Popup, Marker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Radio, Users, Package } from "lucide-react";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function parseCoords(item) {
  if (!item) return null;
  const lat = Number(item.lat);
  const lng = Number(item.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180 || (lat === 0 && lng === 0)) return null;
  return [lat, lng];
}

function FitToPoints({ points, maxZoom = 15 }) {
  const map = useMap();
  const key = (points || []).map((p) => `${p?.[0]},${p?.[1]}`).join("|");
  useEffect(() => {
    const valid = (points || []).filter((p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]));
    const timer = setTimeout(() => {
      map.invalidateSize();
      if (!valid.length) return;
      if (valid.length === 1) map.setView(valid[0], Math.min(15, maxZoom));
      else map.fitBounds(L.latLngBounds(valid), { padding: [48, 48], maxZoom });
    }, 80);
    return () => clearTimeout(timer);
  }, [map, maxZoom, key]);
  return null;
}

function connectionLabel(ping) {
  const ageMs = ping.created_at ? Date.now() - new Date(ping.created_at).getTime() : 0;
  if (Number.isFinite(ageMs) && ageMs > 90 * 1000) {
    const minutes = Math.floor(ageMs / 60_000);
    return minutes > 0 ? `Sem atualização há ${minutes} min` : "Sem atualização há mais de 90 s";
  }
  const quality = {
    good: "Conexão boa",
    unstable: "Conexão instável",
    poor: "Conexão fraca",
    offline: "Offline",
    unknown: "Conexão sem medição",
  }[ping.connection_quality] || "Conexão sem medição";
  const type = ping.network_type === "wifi" ? "Wi‑Fi" : ping.network_type === "cellular" ? "Dados móveis" : null;
  const latency = ping.api_latency_ms != null && Number.isFinite(Number(ping.api_latency_ms))
    ? `${Math.round(Number(ping.api_latency_ms))} ms`
    : null;
  return [quality, type, latency].filter(Boolean).join(" · ");
}

function connectionTone(ping) {
  const ageMs = ping.created_at ? Date.now() - new Date(ping.created_at).getTime() : 0;
  if (Number.isFinite(ageMs) && ageMs > 90 * 1000) return "text-red-700";
  if (ping.connection_quality === "good") return "text-emerald-700";
  if (ping.connection_quality === "poor" || ping.connection_quality === "offline") return "text-red-700";
  if (ping.connection_quality === "unstable") return "text-amber-700";
  return "text-muted-foreground";
}

export default function LiveTracking() {
  const [data, setData] = useState({ deliverers: [], active_deliveries: [] });

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/tracking/live");
      setData(d);
    } catch { /* ignore poll errors */ }
  }, []);

  useLivePoll(load, 15000);

  const deliverers = (data.deliverers || []).filter((p) => parseCoords(p));
  const active = (data.active_deliveries || []).filter((d) => parseCoords(d));

  const center = useMemo(() => {
    const firstDel = parseCoords(deliverers[0]);
    if (firstDel) return firstDel;
    const firstDoc = parseCoords(active[0]);
    if (firstDoc) return firstDoc;
    return [-15.7, -47.9];
  }, [deliverers, active]);

  const markerPoints = useMemo(() => {
    const pts = [];
    for (const p of deliverers) {
      const c = parseCoords(p);
      if (c) pts.push(c);
    }
    for (const d of active) {
      const c = parseCoords(d);
      if (c) pts.push(c);
    }
    return pts;
  }, [deliverers, active]);

  return (
    <div className="space-y-4 w-full min-w-0" data-testid="live-tracking-page">
      <div className="min-w-0">
        <h1 className="font-display font-bold text-xl sm:text-2xl flex items-center gap-2"><Radio size={22} className="shrink-0" /> Rastreio ao vivo</h1>
        <p className="text-sm text-muted-foreground">entregadores em campo e endereços dos documentos com GPS</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Card className="p-4"><div className="text-xs text-muted-foreground">Entregadores ativos</div><div className="text-2xl font-bold">{deliverers.length}</div></Card>
        <Card className="p-4"><div className="text-xs text-muted-foreground flex items-center gap-1"><Package size={12} /> Em rota (mapa)</div><div className="text-2xl font-bold">{active.length}</div></Card>
        <Card className="p-4"><div className="text-xs text-muted-foreground flex items-center gap-1"><Users size={12} /> Atualização</div><div className="text-sm font-medium">A cada 15s</div></Card>
      </div>

      <Card className="overflow-hidden h-[420px]">
        <MapContainer center={center} zoom={markerPoints.length ? 12 : 5} style={{ height: "100%", width: "100%" }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="OSM" />
          <FitToPoints points={markerPoints} />
          {deliverers.map((p) => {
            const coords = parseCoords(p);
            if (!coords) return null;
            return (
            <CircleMarker
              key={p.id || p.deliverer_id}
              center={coords}
              radius={10}
              pathOptions={{ color: "#2C48E0", fillColor: "#2C48E0", fillOpacity: 0.8 }}
            >
              <Tooltip permanent direction="top" offset={[0, -8]}>
                {p.deliverer_name || String(p.deliverer_id).slice(0, 8)}
              </Tooltip>
              <Popup>
                <div className="text-sm font-semibold">{p.deliverer_name || "Entregador"}</div>
                <div className="text-xs text-muted-foreground font-mono">
                  {coords[0].toFixed(5)}, {coords[1].toFixed(5)}
                  {p.accuracy != null ? ` (±${Math.round(p.accuracy)}m)` : ""}
                </div>
                <div className="text-xs mt-1">{p.created_at ? new Date(p.created_at).toLocaleString() : ""}</div>
                <div className={`text-xs mt-1 font-medium ${connectionTone(p)}`}>{connectionLabel(p)}</div>
              </Popup>
            </CircleMarker>
            );
          })}
          {active.map((d) => {
            const coords = parseCoords(d);
            if (!coords) return null;
            return (
            <Marker key={`${d.id}:${coords.join(",")}`} position={coords}>
              <Popup>
                <div className="text-sm font-semibold">{d.number || d.protocol_number}</div>
                <div className="text-xs">{d.recipient_name} — {d.city}</div>
                {d.address ? <div className="text-xs text-muted-foreground">{d.address}</div> : null}
              </Popup>
            </Marker>
            );
          })}
        </MapContainer>
      </Card>

      {deliverers.length > 0 && (
        <Card className="p-4 overflow-x-auto">
          <div className="text-xs font-semibold text-muted-foreground mb-2">Entregadores no mapa</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b">
                <th className="py-2 pr-3">Nome</th>
                <th className="py-2 pr-3">Latitude</th>
                <th className="py-2 pr-3">Longitude</th>
                <th className="py-2 pr-3">Precisão</th>
                <th className="py-2 pr-3">Conexão</th>
                <th className="py-2">Atualizado</th>
              </tr>
            </thead>
            <tbody>
              {deliverers.map((p) => (
                <tr key={p.id || p.deliverer_id} className="border-b border-border/50" data-testid={`deliverer-row-${p.deliverer_id}`}>
                  <td className="py-2 pr-3 font-medium">{p.deliverer_name}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{Number(p.lat).toFixed(5)}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{Number(p.lng).toFixed(5)}</td>
                  <td className="py-2 pr-3 text-xs">{p.accuracy != null ? `±${Math.round(p.accuracy)}m` : "—"}</td>
                  <td className={`py-2 pr-3 text-xs font-medium ${connectionTone(p)}`}>{connectionLabel(p)}</td>
                  <td className="py-2 text-xs">{p.created_at ? new Date(p.created_at).toLocaleTimeString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
